'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { User } from '~/payload-types';
import { checkRole } from '~/payload/authorization/checkRole';
import type { Permissions } from 'payload';

import { rest } from './rest';
import type {
  AuthContext,
  CreateObject,
  ForgotPasswordObject,
  LoginObject,
  Logout,
  ResetPasswordObject,
} from './types';

const Context = createContext({} as AuthContext);

export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [user, setUser] = useState<null | User>();
  const [permissions, setPermissions] = useState<null | Permissions>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const updateUser = async (user: User | null) => {
    setUser(user);
    if (user) {
      setIsAdmin(checkRole(['admin'], user));
    }
  };

  const create = async (create: CreateObject): Promise<User> => {
    const user = await rest(`/api/users`, create);
    if (!user) {
      throw new Error('Failed to retrieve user');
    }
    updateUser(user);
    return user;
  };

  const login = async (login: LoginObject) => {
    const user = await rest(`/api/users/login`, login);
    if (!user) {
      throw new Error('Failed to retrieve user');
    }
    updateUser(user);
    return user;
  };

  const logout = useCallback<Logout>(async () => {
    try {
      await rest(`/api/users/logout`);
    } catch (error: any) {
      console.log('Maybe a payload bug? ' + error.message);
    }
    updateUser(null);
    setIsAdmin(false);
    return;
  }, []);

  // On mount, get user and set
  useEffect(() => {
    const fetchMe = async () => {
      let user = await rest(
        `/api/users/me`,
        {},
        {
          method: 'GET',
        },
      );
      if (user === undefined) {
        user = null;
      }
      updateUser(user);
      setLoading(false);
    };

    void fetchMe();
  }, []);

  const forgotPassword = async (args: ForgotPasswordObject) => {
    await rest(`/api/users/forgot-password`, args);
  };

  const resetPassword = async (args: ResetPasswordObject): Promise<User> => {
    const user = await rest(`/api/users/reset-password`, args);
    if (!user) {
      throw new Error('Failed to retrieve user');
    }
    updateUser(user);
    return user;
  };

  return (
    <Context.Provider
      value={{
        create,
        forgotPassword,
        login,
        logout,
        permissions,
        resetPassword,
        setPermissions,
        setUser,
        isAdmin,
        user,
        loading,
      }}
    >
      {children}
    </Context.Provider>
  );
};

type UseAuth = () => AuthContext;

export const useAuth: UseAuth = () => useContext(Context);
